import React, { useState, useEffect, useRef } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import { TowerEquipment } from '../../types/site';
import { useSiteStore } from '../../store/siteStore';
import * as THREE from 'three';

interface Equipment3DProps {
  towerEquipment: TowerEquipment;
}

const Equipment3D: React.FC<Equipment3DProps> = ({ towerEquipment }) => {
  const { currentSite, setSelectedEquipment, selectedEquipment, showTowerLabels, hoveredEquipmentId, collisions } = useSiteStore();
  const meshRef = useRef<THREE.Group>(null);
  const [isHovered, setIsHovered] = useState(false);
  
  // Check if this equipment is currently selected
  const isSelected = selectedEquipment?.id === towerEquipment.id;
  const isExternallyHovered = hoveredEquipmentId === towerEquipment.id;
  const isColliding = !!collisions[towerEquipment.id]?.length;
  
  if (!currentSite) return null;

  const mountingPoint = currentSite.tower.mountingPoints.find(
    mp => mp.id === towerEquipment.mountingPointId
  );

  if (!mountingPoint) return null;

  const angle = (mountingPoint.angle * Math.PI) / 180;
  const towerRadius = 0.3;
  
  // Use equipment's custom offset or default to 0.1m (10cm away from mounting point)
  const equipmentOffset = towerEquipment.offset !== undefined ? towerEquipment.offset : 0.1;
  const totalRadius = towerRadius + equipmentOffset;
  
  const x = Math.cos(angle) * totalRadius;
  const z = Math.sin(angle) * totalRadius;
  const y = mountingPoint.height;

  // Render local rotation so that world rotation equals stored azimuth.
  // Since parent group rotates by orientationDeg, local = azimuth - orientation.
  const orientationDeg = currentSite.tower.orientationDeg || 0;
  const azimuthRad = (((towerEquipment.azimuth - orientationDeg) * Math.PI) / 180);
  const elevationRad = (towerEquipment.elevation * Math.PI) / 180;

  const isDish = towerEquipment.equipment.type === 'radio-dish';
  const isSector = towerEquipment.equipment.type === 'sector-antenna';
  // Determine dish size (radius in meters) from model; fallback to previous custom size or default
  const getDishSizeForModel = (): number => {
    const id = (towerEquipment.equipment.id || '').toLowerCase();
    const name = (towerEquipment.equipment.name || '').toLowerCase();
    // Known mappings (diameters in meters) → convert to radius
    if (id.includes('5g34') || name.includes('5g-34') || name.includes('34dbi')) return 1.05 / 2;
    if (id.includes('5g30') || name.includes('5g-30') || name.includes('30dbi')) return 0.65 / 2;
    if (name.includes('23dbi')) return 0.378 / 2;
    return (towerEquipment.dishSize || 0.2) / 2;
  };
  const dishSize = getDishSizeForModel();
  const dishThickness = dishSize * 0.25; // Thickness proportional to size
  
  // Sector antenna dimensions (swapped to make it vertical)
  const sectorWidth = 0.3; // 30cm wide
  const sectorHeight = 0.6; // 60cm tall (vertical)
  const sectorDepth = 0.1; // 10cm deep

  const handleClick = (e: any) => {
    e.stopPropagation();
    console.log('Equipment clicked:', towerEquipment.id, towerEquipment.equipment.name);
    setSelectedEquipment(towerEquipment);
  };
  
  const handlePointerDown = (e: any) => {
    // Only handle left mouse button clicks
    if (e.button === 0) {
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      console.log('Equipment pointer down:', towerEquipment.id, towerEquipment.equipment.name);
      setSelectedEquipment(towerEquipment);
    }
  };

  const handlePointerOver = (e: any) => {
    e.stopPropagation();
    setIsHovered(true);
    document.body.style.cursor = 'pointer';
  };

  const handlePointerOut = () => {
    setIsHovered(false);
    document.body.style.cursor = 'default';
  };

  // Get display text (label if exists, otherwise specs)
  const getDisplayText = () => {
    if (towerEquipment.label) {
      return towerEquipment.label;
    }
    const specs = towerEquipment.equipment.specifications;
    if (specs) {
      const parts = [];
      if (specs.frequency) parts.push(specs.frequency);
      if (specs.gain) parts.push(specs.gain);
      return parts.join(' • ') || towerEquipment.equipment.name;
    }
    return towerEquipment.equipment.name;
  };

  return (
    <group ref={meshRef} position={[x, y, z]}>
      <group rotation={[0, azimuthRad, 0]}>
        {isDish ? (
          <>
            {/* Visible mesh with hover and selection highlight */}
            <mesh 
              rotation={[Math.PI / 2 + elevationRad, 0, 0]}
              onClick={handleClick}
              onPointerDown={handlePointerDown}
              onPointerOver={handlePointerOver}
              onPointerOut={handlePointerOut}
            >
              <cylinderGeometry args={[dishSize, dishSize, dishThickness, 32]} />
            <meshStandardMaterial 
              color={isColliding ? "#ef4444" : (isSelected || isHovered || isExternallyHovered ? "#3b82f6" : "#ffffff")} 
              metalness={0.2} 
              roughness={0.6}
              emissive={isColliding ? "#ef4444" : (isSelected || isHovered || isExternallyHovered ? "#3b82f6" : "#000000")}
              emissiveIntensity={isColliding ? 0.6 : (isSelected || isHovered || isExternallyHovered ? 0.4 : 0)}
            />
            </mesh>
            {/* Direction arrow – aligned with dish front */}
            <group rotation={[Math.PI / 2 + elevationRad, 0, 0]}>
              <mesh position={[0, 0, dishThickness * 0.8]} rotation={[-Math.PI / 2, 0, 0]}>
                <cylinderGeometry args={[0.005, 0.005, 0.25, 6]} />
                <meshStandardMaterial color="#3b82f6" transparent opacity={0.5} emissive="#3b82f6" emissiveIntensity={0.2} />
              </mesh>
              <mesh position={[0, 0, dishThickness * 0.8 + 0.16]} rotation={[-Math.PI / 2, 0, 0]}>
                <coneGeometry args={[0.02, 0.06, 12]} />
                <meshStandardMaterial color="#3b82f6" transparent opacity={0.6} emissive="#3b82f6" emissiveIntensity={0.3} />
              </mesh>
            </group>
            {/* Invisible larger hitbox for easier clicking */}
            <mesh 
              rotation={[Math.PI / 2 + elevationRad, 0, 0]}
              onClick={handleClick}
              onPointerDown={handlePointerDown}
              onPointerOver={handlePointerOver}
              onPointerOut={handlePointerOut}
            >
              <cylinderGeometry args={[dishSize * 1.5, dishSize * 1.5, dishThickness * 1.5, 32]} />
              <meshStandardMaterial visible={false} />
            </mesh>
          </>
        ) : isSector ? (
          <>
            {/* Sector antenna - rectangular panel, rotated 90 degrees */}
              <mesh 
                rotation={[elevationRad, Math.PI / 2, 0]}
                onClick={handleClick}
                onPointerDown={handlePointerDown}
                onPointerOver={handlePointerOver}
                onPointerOut={handlePointerOut}
              >
                <boxGeometry args={[sectorWidth, sectorHeight, sectorDepth]} />
                <meshStandardMaterial 
                  color={isColliding ? "#ef4444" : (isSelected || isHovered || isExternallyHovered ? "#3b82f6" : "#ffffff")} 
                  metalness={0.2} 
                  roughness={0.6}
                  emissive={isColliding ? "#ef4444" : (isSelected || isHovered || isExternallyHovered ? "#3b82f6" : "#000000")}
                  emissiveIntensity={isColliding ? 0.6 : (isSelected || isHovered || isExternallyHovered ? 0.4 : 0)}
                />
              </mesh>
            {/* Direction arrow – align with sector front (same rotation as mesh) */}
            <group rotation={[elevationRad, Math.PI / 2, 0]}>
              <mesh position={[0, 0, sectorDepth * 0.8]} rotation={[-Math.PI / 2, 0, 0]}>
                <cylinderGeometry args={[0.005, 0.005, 0.25, 6]} />
                <meshStandardMaterial color="#3b82f6" transparent opacity={0.5} emissive="#3b82f6" emissiveIntensity={0.2} />
              </mesh>
              <mesh position={[0, 0, sectorDepth * 0.8 + 0.16]} rotation={[-Math.PI / 2, 0, 0]}>
                <coneGeometry args={[0.02, 0.06, 12]} />
                <meshStandardMaterial color="#3b82f6" transparent opacity={0.6} emissive="#3b82f6" emissiveIntensity={0.3} />
              </mesh>
            </group>
            {/* Invisible larger hitbox for easier clicking */}
            <mesh 
              rotation={[elevationRad, Math.PI / 2, 0]}
              onClick={handleClick}
              onPointerDown={handlePointerDown}
              onPointerOver={handlePointerOver}
              onPointerOut={handlePointerOut}
            >
              <boxGeometry args={[sectorWidth * 1.2, sectorHeight * 1.2, sectorDepth * 2]} />
              <meshStandardMaterial visible={false} />
            </mesh>
          </>
        ) : (
          <mesh 
            onClick={handleClick}
            onPointerDown={handlePointerDown}
            onPointerOver={handlePointerOver}
            onPointerOut={handlePointerOut}
          >
            <boxGeometry args={[0.15, 0.15, 0.1]} />
            <meshStandardMaterial 
              color={isColliding ? "#ef4444" : (isSelected || isHovered || isExternallyHovered ? "#34d399" : "#10b981")}
              emissive={isColliding ? "#ef4444" : (isSelected || isHovered || isExternallyHovered ? "#10b981" : "#000000")}
              emissiveIntensity={isColliding ? 0.6 : (isSelected || isHovered || isExternallyHovered ? 0.3 : 0)}
            />
            {/* Direction arrow – generic forward */}
            <group>
              <mesh position={[0, 0, 0.18]} rotation={[-Math.PI / 2, 0, 0]}>
                <cylinderGeometry args={[0.005, 0.005, 0.25, 6]} />
                <meshStandardMaterial color="#3b82f6" transparent opacity={0.5} emissive="#3b82f6" emissiveIntensity={0.2} />
              </mesh>
              <mesh position={[0, 0, 0.34]} rotation={[-Math.PI / 2, 0, 0]}>
                <coneGeometry args={[0.02, 0.06, 12]} />
                <meshStandardMaterial color="#3b82f6" transparent opacity={0.6} emissive="#3b82f6" emissiveIntensity={0.3} />
              </mesh>
            </group>
          </mesh>
        )}
      </group>
      
      {/* Label/Specs overlay - always visible when enabled, with lower z-index */}
      {showTowerLabels && (
        <Html 
          position={[0, (isDish ? dishSize : isSector ? sectorHeight : 0.15) * 1.5, 0]} 
          center
          zIndexRange={[0, 0]}
        >
          <div className="bg-black/70 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none">
            {getDisplayText()}
          </div>
        </Html>
      )}
      
      {/* Hover tooltip - only when labels are disabled, with lower z-index */}
      {!showTowerLabels && isHovered && (
        <Html 
          position={[0, (isDish ? dishSize : isSector ? sectorHeight : 0.15) * 1.5, 0]} 
          center
          zIndexRange={[0, 0]}
        >
          <div className="bg-black/90 text-white text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none shadow-lg">
            {getDisplayText()}
          </div>
        </Html>
      )}
    </group>
  );
};

export default React.memo(Equipment3D, (prevProps, nextProps) => {
  return prevProps.towerEquipment.id === nextProps.towerEquipment.id &&
         prevProps.towerEquipment.mountingPointId === nextProps.towerEquipment.mountingPointId &&
         prevProps.towerEquipment.azimuth === nextProps.towerEquipment.azimuth &&
         prevProps.towerEquipment.elevation === nextProps.towerEquipment.elevation &&
         prevProps.towerEquipment.dishSize === nextProps.towerEquipment.dishSize &&
         prevProps.towerEquipment.offset === nextProps.towerEquipment.offset &&
         prevProps.towerEquipment.label === nextProps.towerEquipment.label;
});
